// Payments received per day of one month, for the billing report's daily chart.
// Days are counted in Jakarta time (WIB, UTC+7): paid_at is stored in UTC, so a
// payment late in the evening would otherwise land on the next day.

import type { PublicInvoice } from '@residenone/shared';

const WIB_OFFSET_MS = 7 * 3_600_000;

export interface DayPayments {
  day: number;
  count: number;
  amountCents: number;
  /** After today, in the current month: no data can exist yet. */
  future: boolean;
}

export interface MonthPayments {
  days: DayPayments[];
  totalCount: number;
  totalCents: number;
  /** Day of the month bills issued in this month fall due, or null if none were issued. */
  dueDay: number | null;
}

const jakartaDate = (iso: string) => new Date(Date.parse(iso) + WIB_OFFSET_MS).toISOString().slice(0, 10);

export function dailyPayments(invoices: PublicInvoice[], month: string, today: Date): MonthPayments {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const todayWib = jakartaDate(today.toISOString());

  const days: DayPayments[] = Array.from({ length: daysInMonth }, (_, i) => ({
    day: i + 1,
    count: 0,
    amountCents: 0,
    future: `${month}-${String(i + 1).padStart(2, '0')}` > todayWib,
  }));

  for (const inv of invoices) {
    if (inv.status !== 'PAID' || !inv.paid_at) continue;
    const date = jakartaDate(inv.paid_at);
    if (!date.startsWith(`${month}-`)) continue;
    const d = days[Number(date.slice(8, 10)) - 1]!;
    d.count += 1;
    d.amountCents += inv.total_cents;
  }

  // Most common due day among this month's bills (they normally all share one).
  const dueCounts = new Map<number, number>();
  for (const inv of invoices) {
    if (inv.status === 'VOID' || !inv.issue_date.startsWith(`${month}-`)) continue;
    const dd = Number(inv.due_date.slice(8, 10));
    if (inv.due_date.startsWith(`${month}-`)) dueCounts.set(dd, (dueCounts.get(dd) ?? 0) + 1);
  }
  const dueDay = [...dueCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;

  return {
    days,
    totalCount: days.reduce((s, d) => s + d.count, 0),
    totalCents: days.reduce((s, d) => s + d.amountCents, 0),
    dueDay,
  };
}
